import { Badge, Box, Button, Card, Group, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconArrowRight, IconBook, IconCpu, IconLock, IconPackage, IconRoute, IconSparkles } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { fmtNumber } from "@passio/api-client";
import { AccountButton, AccountCallout } from "../components/AccountCta";
import { brand } from "../theme/brand";
import { Eyebrow, StatCard } from "../theme/components";
import { useNav } from "../lib/nav";
import { useSession } from "../lib/session";

export function HomePage() {
  const { me, hasFeature, toolEnabled } = useSession();
  const nav = useNav();
  const prospect = me?.user.authKind !== "api_key";
  const first = !prospect ? "Passio customer" : me?.user.displayName?.split(" ")[0] || me?.user.username;
  return (
    <Stack gap={40}>
      <Box className="pd-dark" p={{ base: 24, sm: 40 }} style={{ borderRadius: 20 }}>
        <Group justify="space-between" align="flex-end" wrap="wrap" gap="xl">
          <Stack gap="md" maw={640}>
            <Eyebrow>Welcome{first ? `, ${first}` : ""}</Eyebrow>
            <Title order={1} fz={{ base: 36, sm: 52 }} lh={1.05} c="white">
              {brand.tagline}
            </Title>
            <Text className="pd-dim" fz="lg">
              {brand.intro}
            </Text>
            <Text className="pd-dim" fz="sm">
              Set language, region and search filters under <b style={{ color: "#fff" }}>Client settings</b>, and pick the AI model on any page to tune cost, speed or accuracy. Hover the bar at the bottom right after any call to see its headers and request ID.
            </Text>
            <Group>
              {toolEnabled("tour") && hasFeature("text_ai") && (
                <Button component={Link} to="/tour" size="md" color="lime" c="ink.8" leftSection={<IconRoute size={18} />} rightSection={<IconArrowRight size={16} />}>
                  Take a guided tour
                </Button>
              )}
              {prospect && <AccountButton placement="home" size="md" color="gray.0" />}
            </Group>
          </Stack>
          <Stack gap="sm" w={{ base: "100%", sm: 300 }}>
            <StatCard
              caption="your access"
              title={me?.limits.tokensPerDay ? `${fmtNumber(me.limits.tokensPerDay)} tokens / day` : "No daily token cap"}
              stats={[
                { value: String(me?.limits.requestsPerMinute ?? "–"), label: "req / min" },
                { value: me?.user.accessExpiresAt ? new Date(me.user.accessExpiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "∞", label: "until" },
              ]}
            />
          </Stack>
        </Group>
      </Box>

      {nav.map((section) => (
        <Box key={section.verb}>
          <Group justify="space-between" mb="sm" className="pd-rule" pt="md">
            <div>
              <Title order={2} fz={24}>
                {section.verb}
              </Title>
              <Text c="dimmed" fz="sm">
                {section.blurb}
              </Text>
            </div>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {section.items.map((item) => {
              const locked = !hasFeature(item.feature);
              return (
                <Card key={item.path} component={Link} to={item.path} className="pd-tile" style={{ textDecoration: "none", opacity: locked ? 0.6 : 1 }}>
                  <Stack gap={10} h="100%">
                    <Group justify="space-between">
                      <Box p={8} style={{ background: "var(--pd-ink)", borderRadius: 10, color: "var(--pd-lime)", display: "inline-flex" }}>
                        <item.icon size={20} />
                      </Box>
                      {locked ? <IconLock size={16} color="var(--mantine-color-dimmed)" /> : <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />}
                    </Group>
                    <div>
                      <Text fw={700}>{item.label}</Text>
                      <Text fz="sm" c="dimmed">
                        {item.description}
                      </Text>
                    </div>
                    {item.routes.length > 0 && (
                      <Group gap={4} mt="auto">
                        {item.routes.slice(0, 3).map((r) => (
                          <Badge key={r} size="xs" variant="outline" color="gray" tt="none" ff="monospace">
                            {r}
                          </Badge>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      ))}

      {prospect && (
        <AccountCallout
          placement="home_band"
          dark
          title="Ready to build with Passio?"
          body="Create your Passio account, get your own API key and pick a plan. Every request and code sample in this demo works with your key as-is."
        />
      )}

      <Box>
        <Group justify="space-between" mb="sm" className="pd-rule" pt="md">
          <div>
            <Title order={2} fz={24}>
              Build on it
            </Title>
            <Text c="dimmed" fz="sm">
              Headers, models, feedback and SDKs, and what to do when no route fits.
            </Text>
          </div>
        </Group>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          {BUILD.map((b) => (
            <Card key={b.to} component={Link} to={b.to} className={b.dark ? "pd-tile pd-dark" : "pd-tile"} style={{ textDecoration: "none" }}>
              <Stack gap={10} h="100%">
                <Group justify="space-between">
                  <Box p={8} style={{ background: b.dark ? "var(--pd-lime)" : "var(--pd-ink)", borderRadius: 10, color: b.dark ? "var(--pd-ink)" : "var(--pd-lime)", display: "inline-flex" }}>
                    <b.icon size={20} />
                  </Box>
                  <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
                </Group>
                <div>
                  <Text fw={700} c={b.dark ? "white" : undefined}>
                    {b.label}
                  </Text>
                  <Text fz="sm" c="dimmed">
                    {b.description}
                  </Text>
                </div>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      </Box>
    </Stack>
  );
}

const BUILD = [
  { to: "/guide", label: "API guide", description: "Headers, data filters, request IDs and rating results", icon: IconBook },
  { to: "/models", label: "Models", description: "Tune any AI route for cost, speed or accuracy", icon: IconCpu },
  { to: "/developers", label: "Client SDKs", description: "The typed clients behind every page here", icon: IconPackage },
  { to: "/studio", label: "Don't see what you need?", description: "Build your own endpoint in Easy AI Studio, $10/month free", icon: IconSparkles, dark: true },
];
